import React, { useRef, useCallback, useState, useEffect } from 'react';
/* eslint-disable restrict-imports/restrict-imports */
import { makeStyles } from '@material-ui/core';
import { WaveSurfer, WaveForm } from './WaveSurfer';
import Combokeys from 'combokeys';
import TranscriptionAudioAttachmentControls, {
  ControlConstants,
} from './TranscriptionAudioAttachmentComponents/TranscriptionAudioAttachmentControls';
import { copyToClipboard } from '@scale/frontend-lib/clipboard';
import useShortcut from 'react-scale/hooks/useShortcut';
import moment from 'moment';
import 'moment-duration-format';

interface TranscriptionAudioAttachmentViewProps {
  url: string;
  startSelfDestructSequence?: (secs: number) => void;
  responseWindow?: number;
  projectId?: string;
  audioPlaybackPrecision?: number;
}

const formatTime = (ms: number, audioPlaybackPrecision: number) => {
  return moment
    .duration(ms, 'milliseconds')
    .format('mm:ss', { trim: false, precision: audioPlaybackPrecision });
};

function getTimeDisplayFontSize(audioPlaybackPrecision: number) {
  if (audioPlaybackPrecision === 1) {
    return 24;
  } else if (audioPlaybackPrecision === 2) {
    return 22;
  } else if (audioPlaybackPrecision === 3) {
    return 18;
  }
  return 26;
}

export default function TranscriptionAudioAttachmentView(
  props: TranscriptionAudioAttachmentViewProps,
) {
  const classes = useStyles();
  const { url, startSelfDestructSequence, responseWindow } = props;
  const audioPlaybackPrecision = props.audioPlaybackPrecision ?? 0;
  const [loading, setLoading] = useState(true);
  const [volume, setVolume] = useState(0.5);
  const [speed, setSpeed] = useState(1);
  const [playing, setPlaying] = useState(false);
  const [currentTimeRaw, setCurrentTimeRaw] = useState(0); // ms
  const [currentTime, setCurrentTime] = useState(formatTime(0, audioPlaybackPrecision));
  const [durationRaw, setDurationRaw] = useState(0); // ms
  const [duration, setDuration] = useState(formatTime(0, audioPlaybackPrecision));
  const [copiedTime, setCopiedTime] = useState<string | undefined>();
  const timeDisplayFontSize = getTimeDisplayFontSize(audioPlaybackPrecision);

  const wavesurferRef = useRef<$FIXME>(null);

  const handleWSMount = useCallback(
    (waveSurfer: $FIXME) => {
      wavesurferRef.current = waveSurfer;

      wavesurferRef?.current?.load(url);
      wavesurferRef?.current?.setProgressColor('#29CD9C');
      wavesurferRef?.current?.setCursorColor('#29CD9C');
      wavesurferRef?.current?.setWaveColor('#5e5e5e');

      wavesurferRef?.current?.on('ready', () => {
        wavesurferRef.current.setVolume(volume);
        setLoading(false);
        setCurrentTimeRaw(0);
        setDurationRaw(wavesurferRef.current.getDuration() * 1000);
      });

      wavesurferRef?.current?.on('waveform-ready', function () {
        setLoading(false);
      });

      wavesurferRef?.current?.on('loading', () => {
        setLoading(true);
      });

      wavesurferRef?.current?.on('audioprocess', function () {
        if (wavesurferRef.current.isPlaying()) {
          setCurrentTimeRaw(wavesurferRef.current.getCurrentTime() * 1000);
        }
      });

      wavesurferRef?.current?.on('finish', () => {
        setPlaying(false);
      });

      wavesurferRef?.current?.on('seek', () => {
        setCurrentTimeRaw(wavesurferRef.current.getCurrentTime() * 1000);
      });
    },
    [url],
  );

  useEffect(() => {
    setCurrentTime(formatTime(currentTimeRaw, audioPlaybackPrecision));
  }, [currentTimeRaw, audioPlaybackPrecision]);

  useEffect(() => {
    setDuration(formatTime(durationRaw, audioPlaybackPrecision));
  }, [durationRaw, audioPlaybackPrecision]);

  useEffect(() => {
    wavesurferRef?.current?.on('finish', () => {
      setPlaying(false);
      if (responseWindow && startSelfDestructSequence) {
        startSelfDestructSequence(responseWindow);
      }
    });
  }, [responseWindow]);

  const handlePlayPause = useCallback(() => {
    if (wavesurferRef?.current) {
      wavesurferRef.current.playPause();
      setPlaying(!playing);
    }
  }, [playing, setPlaying, wavesurferRef]);

  const handleBack = useCallback(() => {
    if (wavesurferRef?.current) {
      wavesurferRef.current.skipBackward(5);
    }
  }, [wavesurferRef]);

  const handleForward = useCallback(() => {
    if (wavesurferRef?.current) {
      wavesurferRef.current.skipForward(5);
    }
  }, [wavesurferRef]);

  const handleVolumeChange = useCallback(
    (value: number | number[]) => {
      if (wavesurferRef?.current) {
        const newVolume = Array.isArray(value) ? value[0] : value;
        setVolume(newVolume);
        wavesurferRef.current.setVolume(newVolume || 1);
      }
    },
    [wavesurferRef, setVolume],
  );

  const handleSpeed = useCallback(
    (newSpeed: number) => {
      if (wavesurferRef?.current) {
        setSpeed(newSpeed);
        wavesurferRef.current.setPlaybackRate(newSpeed);
      }
    },
    [wavesurferRef, setSpeed],
  );

  const currentTimeFixed = currentTimeRaw.toFixed(3);

  const handleCopy = useCallback(() => {
    const _copiedTime = currentTimeFixed as string;
    setCopiedTime(_copiedTime);
    copyToClipboard(_copiedTime, { copyRaw: true });
    setTimeout(() => {
      setCopiedTime(undefined);
    }, 3000);
  }, [currentTimeFixed]);

  useShortcut({ trigger: 'c', action: handleCopy });

  useEffect(() => {
    return () => {
      wavesurferRef?.current?.destroy();
    };
  }, [url]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const combokeys = new Combokeys(document.body);

    combokeys.bind(['ctrl'], () => {
      handlePlayPause();
      return false;
    });

    combokeys.bind(['shift+left'], () => {
      handleSpeed(Math.max(ControlConstants.speedMin, speed - ControlConstants.speedStep));
      return false;
    });

    combokeys.bind(['shift+right'], () => {
      handleSpeed(Math.min(ControlConstants.speedMax, speed + ControlConstants.speedStep));
      return false;
    });

    combokeys.bind(['left'], () => {
      handleBack();
      return false;
    });

    combokeys.bind(['right'], () => {
      handleForward();
      return false;
    });

    combokeys.bind(['up'], () => {
      handleVolumeChange(
        Math.min(ControlConstants.volumeMax, volume + ControlConstants.volumeStep),
      );
      return false;
    });

    combokeys.bind(['down'], () => {
      handleVolumeChange(
        Math.max(ControlConstants.volumeMin, volume - ControlConstants.volumeStep),
      );
      return false;
    });

    return () => {
      combokeys.detach();
    };
  }, [
    handlePlayPause,
    handleBack,
    handleForward,
    handleVolumeChange,
    handleSpeed,
    ControlConstants,
    speed,
    volume,
  ]);

  return (
    <>
      <TranscriptionAudioAttachmentControls
        handleSpeed={handleSpeed}
        handleVolumeChange={handleVolumeChange}
        handleBack={handleBack}
        handleForward={handleForward}
        handlePlayPause={handlePlayPause}
        loading={loading}
        playing={playing}
        currentTime={currentTime}
        duration={duration}
        speed={speed}
        volume={volume}
        timeFontSize={timeDisplayFontSize}
      />
      <div style={{ marginRight: 20, marginLeft: 20, justifyContent: 'center' }}>
        {loading && <div className={classes.loading}>Loading audio...</div>}
        <WaveSurfer onMount={handleWSMount}>
          <WaveForm id="waveform" className={classes.waveform} />
        </WaveSurfer>
        {copiedTime && `Copied ${copiedTime} to clipboard!`}
      </div>
    </>
  );
}

const useStyles = makeStyles({
  waveformWrapper: {
    width: '100%',
    margin: '0 auto',
    display: 'flex',
  },
  waveform: {
    'flex-grow': 1,
    padding: 8,
    paddingRight: 0,
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    marginLeft: 8,
    marginRight: 8,
  },

  loading: {
    position: 'absolute',
    left: 96,
    color: 'black',
    opacity: 0.6,
  },
});
